import type { ApiClient } from '../../api/client';
import type { StaffingWorker as ApiStaffingWorker } from '../../api/types';
import { workerStatusPresentation } from '../../features/people/public';
import type {
  PeopleOperationsPort,
  PeopleWorker,
} from '../../features/people/public';

function displayDate(value?: string): string {
  return value ? value.slice(0, 10).replaceAll('-', '.') : '—';
}

function mapPeopleWorker(worker: ApiStaffingWorker): PeopleWorker {
  const name = worker.descriptor?.trim() || '이름 미등록';
  const job = worker.primaryJob;
  return {
    id: worker.id,
    personId: worker.person?.id || '',
    workerNumber: worker.workerId || '사번 미등록',
    name,
    initials: Array.from(name)[0] ?? '?',
    photoUrl: worker.person?.profilePhotoUrl,
    organizationId: job?.supervisoryOrganization?.id,
    organization: job?.supervisoryOrganization?.descriptor || '미배치',
    title: job?.businessTitle || job?.jobProfile?.descriptor || job?.descriptor || '—',
    jobProfile: job?.jobProfile?.descriptor,
    jobFamily: job?.jobFamily?.descriptor,
    jobLevel: job?.jobLevel?.descriptor,
    businessTitle: job?.businessTitle,
    jobType: job?.jobType?.descriptor,
    jobTypeId: job?.jobType?.id,
    company: worker.company?.descriptor,
    companyId: worker.company?.id,
    timeType: job?.timeType,
    location: job?.location?.descriptor || '—',
    locationId: job?.location?.id,
    workSpace: job?.workSpace?.descriptor,
    workSpaceId: job?.workSpace?.id,
    status: workerStatusPresentation(worker.status, worker.terminationDate).label,
    email: worker.person?.email || '—',
    phone: worker.person?.phone || '—',
    hireDate: displayDate(worker.hireDate),
    originalHireDate: worker.originalHireDate ? displayDate(worker.originalHireDate) : undefined,
    manager: job?.manager?.descriptor || '—',
    managerId: job?.manager?.id,
    workerType: worker.workerType?.descriptor || '유형 미등록',
    workerTypeId: worker.workerType?.id,
    terminationDate: worker.terminationDate ? displayDate(worker.terminationDate) : undefined,
    additionalJobs: (worker.additionalJobs ?? []).map((additionalJob) => ({
      id: additionalJob.id,
      title: additionalJob.businessTitle || additionalJob.jobProfile?.descriptor || additionalJob.descriptor || '—',
      jobFamily: additionalJob.jobFamily?.descriptor,
      jobLevel: additionalJob.jobLevel?.descriptor,
      jobType: additionalJob.jobType?.descriptor,
      timeType: additionalJob.timeType,
      organization: additionalJob.supervisoryOrganization?.descriptor || '미배치',
      organizationId: additionalJob.supervisoryOrganization?.id,
      location: additionalJob.location?.descriptor || '—',
      workSpace: additionalJob.workSpace?.descriptor,
    })),
  };
}

export function createPeopleOperationsHttpAdapter(client: ApiClient): PeopleOperationsPort {
  return {
    downloadWorkerReport(asOf, workerIds) { return client.downloadWorkerReport(asOf, workerIds); },
    async listWorkers(asOf) {
      return (await client.listAllWorkers('', asOf)).data.map(mapPeopleWorker);
    },
    async listOrganizations(asOf) { return (await client.listAllSupervisoryOrganizations(asOf)).data; },
    async listStaffingChanges() {
      return (await client.listStaffingChanges()).data;
    },
    async listHireReferences(asOf) {
      const [companies, locations, jobs, levels, organizations] = await Promise.all([
        client.listHireCompanies(),
        client.listHireLocations(asOf),
        client.listHireJobProfiles(asOf),
        client.listHireJobLevels(asOf),
        client.listAllSupervisoryOrganizations(asOf),
      ]);
      return {
        companies: companies.data,
        locations: locations.data,
        jobs: jobs.data,
        levels: levels.data,
        organizations: organizations.data,
      };
    },
    async listHireJobTypes(workerType, asOf) {
      return (await client.listHireJobTypes(workerType, asOf)).data;
    },
    async listHireWorkSpaces(locationId, asOf) {
      return (await client.listHireWorkSpaces(locationId, asOf)).data;
    },
    createPerson(input, idempotencyKey) {
      return client.createPerson(input, idempotencyKey as ReturnType<typeof crypto.randomUUID>);
    },
    createHire(personId, input, idempotencyKey) {
      return client.createHire(personId, input, idempotencyKey as ReturnType<typeof crypto.randomUUID>);
    },
    submitHire(eventId, idempotencyKey) {
      return client.submitWorkerLifecycle(eventId, idempotencyKey);
    },
  };
}
